import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { favoritesApi } from '@/services/api/tracking';
import { getDeviceId } from '@/services/api/client';
import { useZoneSlug } from '@/stores/zone.store';

/**
 * FAVORİLER
 *
 * Kaynak backend'dir: misafir için cihaz kimliği, üye için hesap.
 * Böylece kullanıcı telefonundan ekleyip bilgisayarından giriş yaptığında
 * listesi kaybolmaz.
 *
 * İyimser güncelleme kullanılır: kalp anında dolar, istek arkada gider,
 * hata olursa geri alınır.
 */

const SLUGS_KEY = ['favorites', 'slugs'] as const;

export function useFavoriteSlugs() {
  return useQuery({
    queryKey: SLUGS_KEY,
    queryFn: favoritesApi.slugs,
    staleTime: 60_000,
  });
}

export function useFavoriteProducts() {
  const zone = useZoneSlug();

  return useQuery({
    queryKey: ['favorites', 'products', zone ?? 'default'],
    queryFn: () => favoritesApi.list(zone),
    staleTime: 30_000,
  });
}

export function useIsFavorite(slug: string): boolean {
  const { data } = useFavoriteSlugs();
  return data?.includes(slug) ?? false;
}

export function useFavoriteCount(): number {
  const { data } = useFavoriteSlugs();
  return data?.length ?? 0;
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      slug,
      isFavorite,
    }: {
      slug: string;
      isFavorite: boolean;
      productName: string;
    }) => (isFavorite ? favoritesApi.remove(slug) : favoritesApi.add(slug)),

    onMutate: async ({ slug, isFavorite }) => {
      await queryClient.cancelQueries({ queryKey: SLUGS_KEY });
      const previous = queryClient.getQueryData<string[]>(SLUGS_KEY) ?? [];

      queryClient.setQueryData<string[]>(
        SLUGS_KEY,
        isFavorite
          ? previous.filter((entry) => entry !== slug)
          : [slug, ...previous],
      );

      return { previous };
    },

    onError: (_error, _variables, context) => {
      // İyimser güncelleme geri alınır; kullanıcı yanlış durum görmez.
      if (context?.previous) {
        queryClient.setQueryData(SLUGS_KEY, context.previous);
      }

      toast.error('Favori güncellenemedi. Lütfen tekrar deneyin.');
    },

    onSuccess: (_result, { slug, isFavorite, productName }) => {
      track(isFavorite ? 'remove_from_wishlist' : 'add_to_wishlist', {
        item_id: slug,
        item_name: productName,
      });

      toast(isFavorite ? 'Favorilerden çıkarıldı' : 'Favorilere eklendi', {
        description: productName,
        duration: 2000,
      });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  return mutation;
}

/**
 * Giriş sonrası cihaz favorilerini hesaba taşır.
 * İdempotenttir; birden fazla çağrılsa da kopya oluşmaz.
 */
export function useMergeFavorites() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    const slugs = queryClient.getQueryData<string[]>(SLUGS_KEY) ?? [];

    try {
      await favoritesApi.merge(getDeviceId(), slugs);
    } catch {
      // Birleştirme başarısız olsa da giriş akışı bozulmaz.
    }

    await queryClient.invalidateQueries({ queryKey: ['favorites'] });
  }, [queryClient]);
}
